import { ElementRef, EventEmitter, inject, Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { AppStorage } from '@app/common/services/data-api/app-storage.service';
import { DataLookupService } from '@app/common/services/data-api/data-lookup.service';
import { PlexEnvironment } from '@app/env';
import { ProjectsView } from '@app/shared/models/projects-view';
import { GeneralProjectService } from 'app/cem/project/services/general-project.service';
import { ProjectTemplateMatrixService } from 'app/cem/project/services/lookups/project-template-matrix.service';
import { ProjectScreenBehaviorService } from 'app/cem/project/services/project-screenbehavior.service';
import { ColumnMap } from 'app/common/services/data-api/column-map';
import { LookupMapper } from 'app/common/services/data-api/lookup-mapper';
import { AssetTypeIds, DefaultScheduleTemplateNameId, PadConstructionProjectSubTypeId, ProjectSubTypeId, ProjectTypeId } from 'app/shared/data/project-enums';
import { Asset, Project, ProjectTemplate, Table } from 'app/shared/models';
import { NameValueInt, NameValueIntParentInt } from 'app/shared/models/name-value';
import { BehaviorSubject, Observable, empty as observableEmpty, zip as observableZip } from 'rxjs';
import { ProjectCreate } from '../../models/project-create';
import { ProjectDefaults } from '../../models/project-defaults';
import { LookupPlusService } from '../../services/lookups/lookup-plus.service';
import { DrillGroup } from './../../../../shared/models/schedule/drillgroup';
import { AssetWell, BatteryDesignType, IProjectSelectors, ISmallScWell, ProjectApprovalMatrix, ProjectLists, ProjectObject, WellFilters } from './project-create.models';
import { ProjectCreateUrls } from './project-create.urls';

@Injectable()
export class ProjectCreateHelper {
  private _lookupService = inject(DataLookupService);
  private _lookupPlusService = inject(LookupPlusService);
  public store = inject(AppStorage);
  public screenBehaviors = inject(ProjectScreenBehaviorService);
  public matrixService = inject(ProjectTemplateMatrixService);
  public urls = inject(ProjectCreateUrls);
  public generalProjectService = inject(GeneralProjectService);
  public environment = inject(PlexEnvironment);

  private createForm: UntypedFormGroup;
  private Lists: ProjectLists = new ProjectLists();
  public draftEstimateCreated: EventEmitter<any> = new EventEmitter();
  public projectCreated: EventEmitter<Project> = new EventEmitter();
  public projectCreating: BehaviorSubject<{ status: string, percent: number }> = new BehaviorSubject(null);
  public startup: Observable<any>;
  public selectedProjectObject: ProjectObject = {};
  public parentEl: ElementRef;
  public WellFilters: WellFilters = new WellFilters();
  public FilteredLists: ProjectLists = new ProjectLists();
  public servicesLoaded = false;
  public Selections: IProjectSelectors = {
    scenario: null,
    assetTeam: null,
    projectTeam: null,
    areThereSubtypes: false,
    hasProjectTeam: false,
    projectTypeId: 0,
    projectSubTypeId: 0,
    assetTypeId: 0,
    exploreDevelopId: null
  };

  private processZipTasks() {
    this.FilteredLists.TypeCurveAreaIds = this.Lists.TypeCurveAreaIds.slice(0);
    this.FilteredLists.WellProfileIds = this.Lists.WellProfileIds.slice(0);
    this.FilteredLists.TargetFormationIds = this.Lists.TargetFormationIds.slice(0);
    this.FilteredLists.ProjectTypesRequiringWells = this.Lists.ProjectTypesRequiringWells.slice(0);
    // Remove N/A as option
    this.FilteredLists.ExploreDevelopIds = this.Lists.ExploreDevelopIds.filter(x => x.value != 3);
    this.filterAssetTeamItems();
    this.servicesLoaded = true;
  }

  private sortByName(a: { name: string }, b: { name: string }) {
    const genreA = a.name && a.name.toUpperCase();
    const genreB = b.name && b.name.toUpperCase();
    let comparison = 0;
    if (genreA > genreB) {
      comparison = 1;
    } else if (genreA < genreB) {
      comparison = -1;
    }
    return comparison;
  }

  private getKnownLookupData(table: Table, data: NameValueInt[], query: string = null): Promise<NameValueInt[]> {
    if (data.length) {
      let obx = Promise.resolve(data);
      return obx;
    }
    let ob = this._lookupService.getNameValue(table, query, null, true);
    ob.then(payload => {
      payload = payload.sort((a, b) => this.sortByName(a, b));
      this.assignLookupData(payload, data);
    });
    return ob;
  }

  private getLookupData(table: string, data: NameValueInt[], query: string = null): Promise<NameValueInt[]> {
    if (data.length) {
      let obx = Promise.resolve(data);
      return obx;
    }

    let ob = this._lookupService.getNameValue(table, query, null, true);
    ob.then(payload => {
      payload = payload.sort((a, b) => this.sortByName(a, b));
      this.assignLookupData(payload, data);
    });
    return ob;
  }

  private getWellRequiredProjectIds(table: Table, data: NameValueInt[], query: string = null): Promise<NameValueInt[]> {
    if (data.length) {
      let obx = Promise.resolve(data);
      return obx;
    }

    let maps: ColumnMap[] = [];
    maps.push(new ColumnMap('name', 'AssetMappingConfigurationId'));
    maps.push(new ColumnMap('value', 'ProjectTypeId'));
    let mapper: LookupMapper = new LookupMapper(table, maps);
    let ob = this._lookupService.getNameValue(table, query, mapper);
    ob.then(payload => {
      this.assignLookupData(payload, data);
    });
    return ob;
  }

  private getLookupDataWithParents(table: Table, data: NameValueIntParentInt[],
    query: string = null, lookupMap: ColumnMap[]): Promise<NameValueInt[]> {
    if (data.length) {
      let obx = Promise.resolve(data);
      return obx;
    }

    let ob = this._lookupService.get(table, lookupMap, true, true, query);

    ob.then(payload => {
      payload = payload.sort((a, b) => this.sortByName(a, b));
      this.assignLookupData(payload, data);
    });
    return ob;
  }

  private assignLookupData(payload: NameValueInt[], dest: NameValueInt[]) {
    if (!payload) return;
    for (let i = 0; i < payload.length; i++) {
      let p = payload[i];
      if (p) {
        dest.push(p);
      }
    }
  }

  private ensureProjectTemplateSetFromForm(): boolean {
    //populate all selected values in matrix from form values
    let form = this.createForm;
    let dest = this.matrixService.filterValue;
    let changes: boolean = false;
    Object.keys(form.controls).forEach(key => {
      let name = key;
      let value = form.controls[key].value;
      if (value && value.hasOwnProperty('value')) {
        value = value.value;
      }
      if (dest.hasOwnProperty(name) && dest[name] !== value) {
        dest[name] = value;
        changes = true;
      }
    });
    return changes;
  }

  public async init() {
    await this.screenBehaviors.getByProjectTypeId(15);
  }

  public emptyProjectTemplate(assetTeamId: number = null): ProjectTemplate {
    let pt: ProjectTemplate = new ProjectTemplate();
    pt.AssetTeamId = assetTeamId;
    pt.ConstructionTypeId = null;
    pt.DivisionId = null;
    pt.DrillDesignId = null;
    pt.FunctionId = null;
    pt.OperatingUnitId = null;
    pt.OwnerEmployeeId = null;
    pt.ProjectSubTypeId = null;
    pt.ProjectTypeId = null;
    pt.ExploreDevelopId = null;
    pt.ScheduleTemplateNameId = null;
    return pt;
  }

  public start(): Observable<any> {
    let ptMapper: ColumnMap[] = [
      new ColumnMap('name', 'Description'),
      new ColumnMap('value', 'DefaultProjectTeamId'),
      new ColumnMap('parentValue', 'AssetTeamId'),
      new ColumnMap('functionValue', 'FunctionId')
    ];
    let devAreaMapper: ColumnMap[] = [
      new ColumnMap('name', 'Description'),
      new ColumnMap('value', 'AreaId'),
      new ColumnMap('parentValue', 'AssetTeamId')
    ];

    this.startup = observableZip(
      this.getLookupData(this.urls.businessAssociateTable, this.Lists.BusinessAssociateIds),
      this.getLookupDataWithParents(Table.DefaultProjectTeams, this.Lists.ProjectTeamIds, '', ptMapper),
      this.getLookupDataWithParents(Table.Areas, this.Lists.TypeCurveAreaIds, this.urls.TypeCurveAreaFilter, devAreaMapper),
      this.getKnownLookupData(Table.WellProfiles, this.Lists.WellProfileIds),
      this.getKnownLookupData(Table.TargetFormations, this.Lists.TargetFormationIds),
      this.getKnownLookupData(Table.ExploreDevelops, this.Lists.ExploreDevelopIds),
      this.getWellRequiredProjectIds(Table.AssetMappingConfigurations, this.Lists.ProjectTypesRequiringWells, '$filter=ModuleId eq 2'),
      this.matrixService.ready
    );
    if (this.servicesLoaded) {
      this.processZipTasks();
      return observableEmpty();
    } else {
      this.startup.subscribe(r => {
        // START DEBUG INFO ********************************************
        let log: string[] = [];
        Object.getOwnPropertyNames(this.Lists).forEach((fieldName: string, idx2, array2) => {
          log.push('     ' + fieldName + '=' + this.Lists[fieldName].length);
        });
        console.log(`MetaLookupResult Return Item Counts:\n${log.join('\n')}`);
        // END DEBUG INFO ********************************************

        this.processZipTasks();
        return r;
      });
    }
    return this.startup;
  }

  public getVisibility(fieldName: string): boolean {
    return this.screenBehaviors.getVisibility(this.parentEl, fieldName);
  }

  public setup(createForm: UntypedFormGroup, config: { [key: string]: any }, parentEl: ElementRef) {
    this.createForm = createForm;
    this.parentEl = parentEl;
    this.WellFilters = new WellFilters();
    this.Selections.projectTeam = null;
    this.Selections.areThereSubtypes = false;
    this.Selections.hasProjectTeam = false;
    this.Selections.projectTypeId = 0;
    this.Selections.assetTypeId = 0;
    this.Selections.assetTeam = null;
    this.Selections.scenario = null;
  }

  public async getAssetCount(assetTeamId: number): Promise<number> {
    let url = this.urls.wellFilterUrl(
      '', 0, 0, 0, assetTeamId, 0, 0, 0, 0);
    return this._lookupPlusService.getLookupCountAsync<ISmallScWell>(url).then(r => {
      return r.total;
    });
  }

  public async getScenarioWellCount(scenarioId: number): Promise<number> {
    let url = this.urls.wellFilterUrl(
      '',
      0,
      AssetTypeIds.Oil_and_Gas_Well,
      scenarioId,
      this.Selections.assetTeam && this.Selections.assetTeam.AssetTeamId,
      0, 0, 0, 0);
    return this._lookupPlusService.getLookupCountAsync<ISmallScWell>(url).then(r => {
      return r.total;
    });
  }

  public getProjectDefaults(projectTypeId: number, scenarioId: number, wellId: number, drillGroupId: number): Promise<ProjectDefaults> {
    return this.generalProjectService.getProjectDefaults(projectTypeId, scenarioId, wellId, drillGroupId);
  }

  public getProjectView(projectId: number): Promise<ProjectsView> {
    return this.generalProjectService.getProjectView(projectId);
  }

  public async getAssetsWellData(name: string): Promise<AssetWell[]> {
    if (this.Selections.scenario && this.Selections.assetTeam) {
      let projectTypeId: number = this.Selections.projectTypeId;
      let scenarioId: number = this.Selections.scenario.ScenarioId;
      let assetTypeId: number = 22;
      let assetTeamId: number = this.Selections.scenario.AssetTeamId;
      let wellProfileId: number = this.WellFilters.WellProfileId;
      let url = '';

      if (projectTypeId === ProjectTypeId.CompletePostStimOAG || projectTypeId === ProjectTypeId.CompleteStimOAG || projectTypeId === ProjectTypeId.DrillOilGasWell)
        url = this.urls.scheduledWellFilterUrl(name, projectTypeId, assetTeamId, scenarioId);
      else
        url = this.urls.assetWellFilterUrl(name, projectTypeId, assetTypeId, scenarioId, assetTeamId, wellProfileId);

      const payload = await this._lookupPlusService.getLookupCustom(url);
      return payload.value;
    } else {
      return await Promise.resolve(null);
    }
  }

  public async getDrillGroupsData(name: string): Promise<DrillGroup[]> {
    if (this.Selections.scenario) {
      let scenarioId: number = this.Selections.scenario.ScenarioId;
      let projectTypeId: number = this.Selections.projectTypeId;
      let scheduleTemplateNameId: number = 0;
      let filter = ``;
      switch (projectTypeId) {
        case ProjectTypeId.Flowline:
          scheduleTemplateNameId = DefaultScheduleTemplateNameId.WellConnectFlowline;
          break;
        case ProjectTypeId.WellPadFacility:
          scheduleTemplateNameId = DefaultScheduleTemplateNameId.WellConnectWellPadFacility;
          filter = ` and not contains(ProjectTypes, '[${projectTypeId}]')`;
          break;
        case ProjectTypeId.WellHookup:
          scheduleTemplateNameId = DefaultScheduleTemplateNameId.WellConnectWellHookup;
          filter = ` and not contains(ProjectTypes, '[${projectTypeId}]')`;
          break;
      }
      let url = `DrillGroups?$skip=0&$top=40&$orderby=Description&$select=DrillGroupId,Description&$filter=ScenarioId eq ${scenarioId} and contains(Description, '${name}')`;

      if (scheduleTemplateNameId > 0)
        url = `ScheduledDrillGroupProjectsView?$skip=0&$top=40&$orderby=Description&$select=DrillGroupId,Description&$filter=ScenarioId eq ${scenarioId} and ScheduleTemplateNameId eq ${scheduleTemplateNameId} and contains(Description, '${name}')${filter}`;

      const payload = await this._lookupPlusService.getLookupCustom(url);
      return payload.value;
    } else {
      return await Promise.resolve(null);
    }
  }

  public async getAfeProjectTeams(assetTeamId: number, projectTypeId: number, projectSubTypeId: number, exploreDevelopId: number): Promise<ProjectApprovalMatrix[]> {
    try {
      if (!projectTypeId) return [];
      let url = `ProjectApprovalMatrix?$filter=AssetTeamId eq ${assetTeamId} and ProjectTypeId eq ${projectTypeId}`;
      if (projectSubTypeId) {
        url += ` and (ProjectSubTypeId eq ${projectSubTypeId} or ProjectSubTypeId eq null)`;
      }
      if (exploreDevelopId) {
        url += ` and (ExploreDevelopId eq ${exploreDevelopId} or ExploreDevelopId eq null) `;
      }
      const payload = await this._lookupPlusService.getLookupCustom(url);
      return payload.value;
    } catch (error) {
      console.error("Failed to fetch AFE project teams API:", error);
      return [];
    }
  }

  public async getTankBatteryAssets(name: string): Promise<Asset[]> {
    let projectSubTypeId: number = this.Selections.projectSubTypeId;
    let assetTeamId = this.store.knownStore.assetTeam.AssetTeamId;
    let url = '';
    if (projectSubTypeId === ProjectSubTypeId.InitialBuild || projectSubTypeId === PadConstructionProjectSubTypeId.TankBattery) {
      let select = `AssetsProjectAssetMappingView?$top=20`;
      url = `AssetsProjectAssetMappingView?$filter=AssetTeamId eq ${assetTeamId} and IsActive eq true and AssetTypeId eq 35 and contains(AssetName, '${name}') and not contains(ProjectSubTypes, '${projectSubTypeId}') &$select=AssetName,AssetId,LegacyAssetId`;
    }
    else {
      url = `Assets?$filter=AssetTeamId eq ${assetTeamId} and IsActive eq true and AssetTypeId eq 35 and contains(AssetName, '${name}') &$select=AssetName,AssetId,LegacyAssetId,TankBatteryDesignId`;
    }

    const payload = await this._lookupPlusService.getLookupCustom(url);
    return payload.value;
  }

  public async getPPIDAssets(name: string): Promise<Asset[]> {
    let url = `Assets?$filter=AssetTeamId eq 3 and IsActive eq true and AssetTypeId eq 53 and contains(AssetName, '${name}') &$select=AssetName,AssetId`;

    const payload = await this._lookupPlusService.getLookupCustom(url);
    return payload.value;
  }

  public getAllLists(filtered: boolean = true): Map<string, any[]> {
    let matrixItems = this.matrixService.getLists(true);
    let source = filtered ? this.FilteredLists : this.Lists;
    let controls = this.createForm && this.createForm.controls;

    if (controls) {
      matrixItems.forEach((_value: any[], fieldName: string) => {
        if (!controls[fieldName]) {
          matrixItems.delete(fieldName);
        }
      });
    }

    Object.getOwnPropertyNames(source).forEach((fieldName: string, idx, array) => {
      if (!controls || controls[fieldName]) {
        matrixItems.set(fieldName, source[fieldName]);
      }
    });

    return matrixItems;
  }

  // Uses the selected D&C project SubType = horizontal or vertical and fetches the Id from the FilteredLists.WellProfileIds
  public filterWellProfiles(name: string) {
    if (!name) {
      this.FilteredLists.WellProfileIds = this.Lists.WellProfileIds.slice(0);
    } else {
      this.FilteredLists.WellProfileIds = this.Lists.WellProfileIds.filter(s => s.name === name);
    }
  }

  public filterAssetTeamItems() {
    let assetTeamId: number = this.Selections.assetTeam && this.Selections.assetTeam.AssetTeamId;
    if (!assetTeamId) {
      this.FilteredLists.TypeCurveAreaIds = [];
      this.FilteredLists.ProjectTeamIds = [];
      return;
    }
    this.FilteredLists.TypeCurveAreaIds = this.Lists.TypeCurveAreaIds.filter(s => s.parentValue === assetTeamId);
    let projectTeams = this.Lists.ProjectTeamIds.filter(s => s.parentValue === assetTeamId && s.name !== 'Custom');
    projectTeams.unshift({
      name: 'Custom',
      value: null,
      parentValue: assetTeamId,
      functionValue: null
    });
    this.FilteredLists.ProjectTeamIds = projectTeams;
  }

  public async createNewProjects(createPostStimProject: boolean = false, createWellsProjectsFromGroup: boolean = false, projectDefaults: ProjectDefaults, allocatedAssets: Array<number>, drillGroupId: number, additionalDrillgroups: Array<number>): Promise<Project[]> {
    this.projectCreating.next({ status: 'creating project...', percent: 0 });
    this.ensureProjectTemplateSetFromForm();
    let projectTemplate = this.matrixService.filterValue;
    let form: any = this.createForm.getRawValue();
    if (form.ProjectTeamId) {
      if (form.ProjectTeamIds.value === null || form.ProjectTeamIds.value === undefined) {
        this.Selections.hasProjectTeam = false;
      } else {
        this.Selections.hasProjectTeam = true;
      }
    }

    let capExDefaultValue = await this.getCapExDefaultValue(form);
    let project: Project | any = {
      CapitalOperationalExpendatureId: capExDefaultValue,
      ExploreDevelopId: form.ExploreDevelopIds ? form.ExploreDevelopIds.value : 3, // N/A
      Name: form.projectName,
      Location: form.Location,
      FunctionId: form.FunctionIds ? form.FunctionIds.value : null,
      AssetTeamId: this.Selections.assetTeam.AssetTeamId,
      ProjectTypeId: form.ProjectTypeIds.value,
      ProjectSubTypeId: form.ProjectSubTypeIds ? form.ProjectSubTypeIds.value : null,
      DivisionId: form.DivisionIds ? form.DivisionIds.value : null,
      BusinessAssociateId: form.BusinessAssociateId ? form.BusinessAssociateId.value : null,
      ScenarioId: this.Selections.scenario && this.Selections.scenario.ScenarioId,
      ConstructionSequenceId: form.ConstructionSequenceIds && form.ConstructionSequenceIds.value,
      ConstructionTypeId: form.ConstructionTypeIds && form.ConstructionTypeIds.value,
      TeamDevelopmentAreaId: (this.WellFilters.well && this.WellFilters.well.DevelopmentAreaId),
      NeedByDate: form.NeedByDate as Date,
      ProjectDescription: form.projectDescription,
      DefaultProjectTeamId: form.ProjectTeamIds ? form.ProjectTeamIds.value : null,
      StartDate: form.StartDate as Date,
      ScheduleTemplateNameId: projectDefaults && projectDefaults.ScheduleTemplateNameId,
      AfeApprovalTeam: form.AfeApprovalTeam ? form.AfeApprovalTeam.ProjectTeam : null,
      BatteryDesignTypeId: form.BatteryDesignTypes ? form.BatteryDesignTypes.BatteryDesignTypeId : null
    };

    const createProject = new ProjectCreate({
      CreatedProject: project,
      AssetId: this.WellFilters.assetId,
      WellId: form.WellIds ? form.WellIds.WellId : null,
      DrillGroupId: drillGroupId > 0 ? drillGroupId : null,
      DrillDesignId: projectTemplate.DrillDesignId,
      DefaultStandardConfigurationId: projectTemplate.DefaultStandardConfigurationId,
      AllocatedAssets: allocatedAssets,
      AdditionalDrillGroups: additionalDrillgroups,
      TankBatteryAssetId: form.TankBatteryAssetId ? form.TankBatteryAssetId.AssetId : null,
      PadAssetId: form.PadAssetId ? form.PadAssetId.AssetId : null,
      ReturnAsArray: true
    });

    let newProjects: Project[] = await this.generalProjectService.postProject(createProject, this.environment.useNewEngine || false, createPostStimProject, createWellsProjectsFromGroup);

    newProjects.forEach(x => this.projectCreated.emit(x))

    return newProjects;
  }

  private async getProjectWBSType(projectTypeId: number, projectSubTypeId: number, exploreDevelopId: number) {
    try {
      if (!projectTypeId) return [];
      let url = `WBStoPlexProjectTypesMapping?$filter=ProjectType eq ${projectTypeId}`
      if (projectSubTypeId) {
        url += ` and (ProjectSubType eq ${projectSubTypeId} or ProjectSubType eq null)`;
      }
      else {
        url += ` and (ProjectSubType eq null)`;
      }
      if (exploreDevelopId) {
        url += ` and (ExploreDevelop eq ${exploreDevelopId} or ExploreDevelop eq null) `;
      }
      const payload = await this._lookupPlusService.getLookupCustom(url);
      return (payload && payload.value) || [];
    }
    catch (error) {
      console.error("Failed to fetch WBS project types", error);
      return [];
    }
  }

  private async getCapExDefaultValue(form: any): Promise<number> {
    const wbsToPlexProjectTypesMapping = await this.getProjectWBSType(
      form.ProjectTypeIds ? form.ProjectTypeIds.value : null,
      form.ProjectSubTypeIds ? form.ProjectSubTypeIds.value : null,
      form.ExploreDevelopIds ? form.ExploreDevelopIds.value : null
    );
    const gfcmCode = wbsToPlexProjectTypesMapping && wbsToPlexProjectTypesMapping[0] && wbsToPlexProjectTypesMapping[0].GFCMCodeInfo;

    switch (gfcmCode) {
      case GfcmCodeEnum.CapEx:
        return Number(CapitalOperationalExpendatureEnum.CapEx);
      case GfcmCodeEnum.OpEx:
        return Number(CapitalOperationalExpendatureEnum.OpEx);
      default:
        return null;
    }
  }

  public async getBatteryDesignTypeList(): Promise<BatteryDesignType[]> {
    const url = `BatteryDesignTypes?$select=BatteryDesignTypeId,Description`;
    const payload = await this._lookupPlusService.getLookupCustom(url);
    return payload.value;
  }
}

export enum CapitalOperationalExpendatureEnum {
  CapEx = 1,
  OpEx = 2
}

export enum GfcmCodeEnum {
  CapEx = '.CAP',
  OpEx = '.EXP'
}